import { useEffect, useState, type ReactElement } from 'react';
import type {
  ResumoLoteGenealogiaArestaView,
  ResumoLoteInsumoView,
  ResumoLoteProcessoExecutadoView,
  ResumoLoteQrView,
  ResumoLoteRetrabalhoView,
  ResumoLoteTrecho,
} from '@pieltec/contracts';
import { Skeleton } from '../../components/ui/skeleton';
import { ErroResumoQr, cartaoControleApi } from '../../lib/rastreamento/cartao-controle-api';
import { formatarDataHoraOperacional, formatarQuantidadeDecimal } from '../../lib/data-hora-operacional';
import { etapasDoRoteiro } from '../../lib/rastreamento/etapa-roteiro';

export type EstadoResumoQr = 'carregando' | 'acesso-negado' | 'nao-encontrado' | 'erro' | 'pronto';

/** Falha de leitura reduzida ao que decide o estado: o status HTTP (null = sem resposta). */
export interface FalhaResumoQr { readonly status: number | null }

export const TEXTO_ACESSO_NEGADO = 'Você não tem permissão para consultar este registro.';
export const TEXTO_NAO_ENCONTRADO = 'Registro não encontrado.';
export const TEXTO_FALHA_LEITURA = 'Não foi possível carregar os dados. Verifique a conexão e tente novamente.';
export const TEXTO_TRUNCADO = 'Exibindo os primeiros 500 registros.';

export function falhaDeLeitura(cause: unknown): FalhaResumoQr {
  return { status: cause instanceof ErroResumoQr ? cause.status : null };
}

/** H-04: o estado vem do status HTTP, nunca de texto da mensagem; qualquer falha prevalece sobre dado antigo. */
export function estadoResumoQr(input: { readonly carregando: boolean; readonly falha: FalhaResumoQr | null; readonly resumo: ResumoLoteQrView | null }): EstadoResumoQr {
  if (input.carregando) return 'carregando';
  if (input.falha) {
    if (input.falha.status === 401 || input.falha.status === 403) return 'acesso-negado';
    return input.falha.status === 404 ? 'nao-encontrado' : 'erro';
  }
  return input.resumo ? 'pronto' : 'erro';
}

const ROTULO_ORIGEM_INSUMO: Record<ResumoLoteInsumoView['origem'], string> = {
  APONTAMENTO_INSUMO: 'Lote de insumo',
  SAIDA_TINTA: 'Saída de tinta',
  DEVOLUCAO_TINTA: 'Devolução de tinta',
  PERDA_TINTA: 'Perda de tinta',
  RATEIO_TINTA: 'Rateio de tinta',
};

export interface LinhaProcesso {
  readonly chave: string;
  readonly trecho: ResumoLoteTrecho;
  readonly processo: string;
  readonly celula: string;
  readonly operador: string;
  readonly inicio: string;
  readonly fim: string;
  readonly quantidade: string | null;
}

export interface LinhaRetrabalho {
  readonly osId: string;
  readonly texto: string;
}

export interface LinhaInsumo {
  readonly chave: string;
  readonly trecho: ResumoLoteTrecho;
  readonly origem: string;
  readonly material: string;
  readonly loteInsumo: string;
  readonly quantidade: string;
  readonly data: string;
}

export interface LinhaAresta {
  readonly chave: string;
  readonly relacao: string;
  readonly tipo: string;
  readonly loteOrigem: string;
  readonly loteDestino: string;
  readonly data: string;
  readonly revisao: string;
}

export interface GrupoPorTrecho<T> {
  readonly trecho: ResumoLoteTrecho;
  readonly linhas: readonly T[];
}

function listaDeEtapas(etapas: readonly unknown[]): readonly string[] {
  return etapasDoRoteiro(etapas).map((etapa) => etapa.nome);
}

function linhaDeProcesso(processo: ResumoLoteProcessoExecutadoView): LinhaProcesso {
  return {
    chave: `${processo.tipo}-${processo.id}`,
    trecho: processo.trecho,
    processo: processo.processoNome ?? `Etapa ${processo.etapaOrdem}`,
    celula: processo.celula ? `${processo.celula.codigo} · ${processo.celula.nome}` : 'Célula não informada',
    operador: processo.operador?.nome ?? 'Operador não informado',
    inicio: formatarDataHoraOperacional(processo.inicio),
    fim: processo.fim ? formatarDataHoraOperacional(processo.fim) : 'Fim não registrado',
    quantidade: processo.quantidadeBoa === null || processo.quantidadeNaoConforme === null
      ? null
      : `Produzidas ${formatarQuantidadeDecimal(processo.quantidadeBoa, 'un')} · Não conformes ${formatarQuantidadeDecimal(processo.quantidadeNaoConforme, 'un')}`,
  };
}

function linhaDeRetrabalho(retrabalho: ResumoLoteRetrabalhoView): LinhaRetrabalho {
  return {
    osId: retrabalho.osId,
    texto: `Parte do lote passou por retrabalho: ${formatarQuantidadeDecimal(retrabalho.quantidadeNc, 'un')} (NC: ${retrabalho.motivoNc})`,
  };
}

function linhaDeInsumo(insumo: ResumoLoteInsumoView): LinhaInsumo {
  return {
    chave: `${insumo.origem}-${insumo.id}`,
    trecho: insumo.trecho,
    origem: ROTULO_ORIGEM_INSUMO[insumo.origem],
    material: `${insumo.materialCodigo} · ${insumo.materialNome}`,
    loteInsumo: insumo.loteInsumoCodigo ?? 'Lote de insumo não informado',
    quantidade: formatarQuantidadeDecimal(insumo.quantidade, insumo.unidade),
    data: formatarDataHoraOperacional(insumo.registradoEm),
  };
}

function linhaDeAresta(aresta: ResumoLoteGenealogiaArestaView, indice: number): LinhaAresta {
  return {
    chave: `${aresta.relacao}-${aresta.loteOrigem.loteId}-${aresta.loteDestino.loteId}-${indice}`,
    relacao: aresta.relacao === 'ORIGEM' ? 'Origem' : 'Destino',
    tipo: aresta.tipo === 'SPLIT' ? 'Divisão' : 'União',
    loteOrigem: aresta.loteOrigem.loteCodigo,
    loteDestino: aresta.loteDestino.loteCodigo,
    data: formatarDataHoraOperacional(aresta.registradaEm),
    revisao: aresta.revisaoUsada ? `Revisão usada: ${aresta.revisaoUsada.codigo}` : 'Revisão não informada para este trecho',
  };
}

/** Agrupa preservando a ordem recebida (primeira aparição de cada trecho). */
export function agruparPorTrecho<T extends { readonly trecho: ResumoLoteTrecho }>(linhas: readonly T[]): readonly GrupoPorTrecho<T>[] {
  const grupos = new Map<string, { trecho: ResumoLoteTrecho; linhas: T[] }>();
  for (const linha of linhas) {
    const grupo = grupos.get(linha.trecho.loteId);
    if (grupo) grupo.linhas.push(linha);
    else grupos.set(linha.trecho.loteId, { trecho: linha.trecho, linhas: [linha] });
  }
  return [...grupos.values()];
}

export function projetarResumoQr(resumo: ResumoLoteQrView) {
  const snapshot = resumo.edicao.snapshot;
  return {
    lote: snapshot.lote.codigo,
    op: snapshot.op.numero === null ? 'OP não informada' : `OP ${snapshot.op.numero}`,
    situacao: snapshot.op.status ?? 'Não informada',
    quantidade: snapshot.op.quantidade.estado === 'NAO_INFORMADA' ? 'Quantidade não informada' : formatarQuantidadeDecimal(snapshot.op.quantidade.valor, snapshot.op.quantidade.unidade),
    revisao: snapshot.revisao.codigo ?? 'Revisão não informada para este lote legado',
    edicaoLida: `Edição lida: CP-${resumo.edicao.sequencia}`,
    edicaoAtual: resumo.edicaoAtualSequencia === null ? null : `Edição atual: CP-${resumo.edicaoAtualSequencia}`,
    substituidaPor: resumo.substituidaPorSequencia === null ? null : `Substituído por edição ${resumo.substituidaPorSequencia}`,
    // A API não grava quando a edição ficou obsoleta; createdAt é a data de EMISSÃO do cartão, então o texto não afirma "desde" (WR-05).
    bannerDesatualizado: resumo.statusAtual === 'DESATUALIZADO'
      ? `Cartão emitido em ${formatarDataHoraOperacional(resumo.edicao.createdAt)}. Os dados do lote mudaram depois da emissão; reimprima a edição atual antes de usar o papel.`
      : null,
    truncado: resumo.historico.truncado,
    roteiro: listaDeEtapas(snapshot.roteiro.etapas),
    processos: resumo.historico.processos.map(linhaDeProcesso),
    retrabalhos: resumo.historico.retrabalhos.map(linhaDeRetrabalho),
    insumos: resumo.historico.insumos.map(linhaDeInsumo),
    genealogia: resumo.historico.genealogia.map(linhaDeAresta),
  };
}

function Tabela({ legenda, colunas, children }: { readonly legenda: string; readonly colunas: readonly { readonly titulo: string; readonly numerica?: boolean }[]; readonly children: ReactElement[] }): ReactElement {
  return (
    <table className="w-full table-fixed border-collapse text-left">
      <caption className="sr-only">{legenda}</caption>
      <thead>
        <tr>{colunas.map((coluna) => <th key={coluna.titulo} scope="col" className={`p-2 font-semibold text-muted-foreground ${coluna.numerica ? 'text-right' : ''}`}>{coluna.titulo}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

const CELULA = 'min-w-0 break-words border-t border-border p-2';
const CELULA_NUMERICA = `${CELULA} text-right tabular-nums`;

function CabecalhoTrecho({ trecho, loteLidoId }: { readonly trecho: ResumoLoteTrecho; readonly loteLidoId: string }): ReactElement {
  return <h3 className="mt-4 break-words font-semibold">{trecho.loteId === loteLidoId ? `Lote lido: ${trecho.loteCodigo}` : `Trecho: lote ${trecho.loteCodigo}`}</h3>;
}

function GruposPorTrecho<T extends { readonly trecho: ResumoLoteTrecho }>({ linhas, loteLidoId, renderizar }: { readonly linhas: readonly T[]; readonly loteLidoId: string; readonly renderizar: (grupo: readonly T[]) => ReactElement }): ReactElement {
  const grupos = agruparPorTrecho(linhas);
  const rotular = grupos.some((grupo) => grupo.trecho.loteId !== loteLidoId);
  return (
    <>
      {grupos.map((grupo) => (
        <div key={grupo.trecho.loteId} className="min-w-0">
          {rotular && <CabecalhoTrecho trecho={grupo.trecho} loteLidoId={loteLidoId} />}
          {renderizar(grupo.linhas)}
        </div>
      ))}
    </>
  );
}

export function ResumoLoteConteudo({ resumo }: { readonly resumo: ResumoLoteQrView }): ReactElement {
  const view = projetarResumoQr(resumo);
  const loteLidoId = resumo.loteId;
  return (
    <main className="rastreamento-page" data-density="touch">
      <header className="min-w-0">
        <p className="rastreamento-kicker">Resumo autenticado do QR</p>
        <p className="font-semibold">{view.situacao}</p>
        <h1 className="break-words">{view.lote}</h1>
        <p className="break-words">{view.op}</p>
        <p className="break-words">{view.edicaoLida}</p>
        {view.edicaoAtual && <p>{view.edicaoAtual}</p>}
        {view.substituidaPor && <p>{view.substituidaPor}</p>}
        {view.bannerDesatualizado && <p role="alert" className="rastreamento-alerta">{view.bannerDesatualizado}</p>}
      </header>
      <dl className="rastreamento-resumo">
        <div><dt>Quantidade</dt><dd>{view.quantidade}</dd></div>
        <div><dt>Revisão usada</dt><dd className="break-words">{view.revisao}</dd></div>
      </dl>
      <section className="rastreamento-secao">
        <h2>Roteiro</h2>
        {view.roteiro.length ? <ul>{view.roteiro.map((nome, indice) => <li key={`${indice}-${nome}`} className="break-words">{nome}</li>)}</ul> : <p>Nenhuma etapa de roteiro informada.</p>}
      </section>
      <section className="rastreamento-secao">
        <h2>Processos executados</h2>
        {view.truncado.processos && <p>{TEXTO_TRUNCADO}</p>}
        {view.processos.length ? (
          <GruposPorTrecho linhas={view.processos} loteLidoId={loteLidoId} renderizar={(grupo) => (
            <Tabela legenda="Processos executados" colunas={[{ titulo: 'Processo' }, { titulo: 'Célula' }, { titulo: 'Operador' }, { titulo: 'Início', numerica: true }, { titulo: 'Fim', numerica: true }, { titulo: 'Quantidade', numerica: true }]}>
              {grupo.map((linha) => (
                <tr key={linha.chave}>
                  <td className={CELULA}>{linha.processo}</td>
                  <td className={CELULA}>{linha.celula}</td>
                  <td className={CELULA}>{linha.operador}</td>
                  <td className={CELULA_NUMERICA}>{linha.inicio}</td>
                  <td className={CELULA_NUMERICA}>{linha.fim}</td>
                  <td className={CELULA_NUMERICA}>{linha.quantidade ?? 'Quantidade não associada'}</td>
                </tr>
              ))}
            </Tabela>
          )} />
        ) : <p>Nenhum processo executado registrado.</p>}
      </section>
      {view.retrabalhos.length > 0 && <section className="rastreamento-secao">
        <h2>Retrabalho</h2>
        <ul>{view.retrabalhos.map((retrabalho) => <li key={retrabalho.osId} className="break-words">{retrabalho.texto}</li>)}</ul>
      </section>}
      <section className="rastreamento-secao">
        <h2>Insumos e tintas</h2>
        {view.truncado.insumos && <p>{TEXTO_TRUNCADO}</p>}
        {view.insumos.length ? (
          <GruposPorTrecho linhas={view.insumos} loteLidoId={loteLidoId} renderizar={(grupo) => (
            <Tabela legenda="Insumos e tintas" colunas={[{ titulo: 'Origem' }, { titulo: 'Material' }, { titulo: 'Lote de insumo' }, { titulo: 'Quantidade', numerica: true }, { titulo: 'Data', numerica: true }]}>
              {grupo.map((linha) => (
                <tr key={linha.chave}>
                  <td className={CELULA}>{linha.origem}</td>
                  <td className={CELULA}>{linha.material}</td>
                  <td className={CELULA}>{linha.loteInsumo}</td>
                  <td className={CELULA_NUMERICA}>{linha.quantidade}</td>
                  <td className={CELULA_NUMERICA}>{linha.data}</td>
                </tr>
              ))}
            </Tabela>
          )} />
        ) : <p>Nenhum insumo ou tinta registrado.</p>}
      </section>
      <section className="rastreamento-secao">
        <h2>Genealogia</h2>
        {view.truncado.genealogia && <p>{TEXTO_TRUNCADO}</p>}
        {view.genealogia.length ? (
          <Tabela legenda="Genealogia do lote" colunas={[{ titulo: 'Relação' }, { titulo: 'Tipo' }, { titulo: 'Lote de origem' }, { titulo: 'Lote de destino' }, { titulo: 'Data', numerica: true }, { titulo: 'Revisão' }]}>
            {view.genealogia.map((linha) => (
              <tr key={linha.chave}>
                <td className={CELULA}>{linha.relacao}</td>
                <td className={CELULA}>{linha.tipo}</td>
                <td className={CELULA}>{linha.loteOrigem}</td>
                <td className={CELULA}>{linha.loteDestino}</td>
                <td className={CELULA_NUMERICA}>{linha.data}</td>
                <td className={CELULA}>{linha.revisao}</td>
              </tr>
            ))}
          </Tabela>
        ) : <p>Nenhuma origem registrada.</p>}
      </section>
    </main>
  );
}

export function ResumoLoteCarregando(): ReactElement {
  return (
    <main className="rastreamento-page" aria-busy="true" data-density="touch">
      <p className="sr-only" role="status">Carregando resumo do lote…</p>
      <header data-slot="skeleton-cabecalho" className="space-y-2" aria-hidden="true">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
      </header>
      <div data-slot="skeleton-corpo" className="mt-4 space-y-4" aria-hidden="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </main>
  );
}

/** Estados sem dado do lote: 401/403 e 404 não recebem o resumo, logo não podem exibir metadados (H-04). */
export function ResumoLoteSemDados({ estado, onTentarNovamente }: { readonly estado: Exclude<EstadoResumoQr, 'carregando' | 'pronto'>; readonly onTentarNovamente: () => void }): ReactElement {
  if (estado === 'acesso-negado') return <main className="rastreamento-page"><h1>Resumo do lote</h1><p role="alert">{TEXTO_ACESSO_NEGADO}</p></main>;
  if (estado === 'nao-encontrado') return <main className="rastreamento-page"><h1>Resumo do lote</h1><p role="alert">{TEXTO_NAO_ENCONTRADO}</p></main>;
  return (
    <main className="rastreamento-page">
      <h1>Resumo do lote</h1>
      <p role="alert">{TEXTO_FALHA_LEITURA}</p>
      <button type="button" onClick={onTentarNovamente}>Tentar novamente</button>
    </main>
  );
}

export function ResumoLotePage(): ReactElement {
  const token = new URLSearchParams(window.location.search).get('qr')?.trim() ?? '';
  const [resumo, setResumo] = useState<ResumoLoteQrView | null>(null);
  const [falha, setFalha] = useState<FalhaResumoQr | null>(null);
  const [carregando, setCarregando] = useState(true);
  const carregar = async (): Promise<void> => {
    setCarregando(true);
    setFalha(null);
    try {
      if (!token) throw new ErroResumoQr(404, 'Código não informado.');
      setResumo(await cartaoControleApi.resumoQr(token));
    } catch (cause) {
      setResumo(null);
      setFalha(falhaDeLeitura(cause));
    } finally {
      setCarregando(false);
    }
  };
  useEffect(() => { void carregar(); }, [token]);
  const estado = estadoResumoQr({ carregando, falha, resumo });
  if (estado === 'carregando') return <ResumoLoteCarregando />;
  if (estado !== 'pronto' || !resumo) return <ResumoLoteSemDados estado={estado === 'pronto' ? 'erro' : estado} onTentarNovamente={() => void carregar()} />;
  return <ResumoLoteConteudo resumo={resumo} />;
}
